import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useParams, useNavigate } from "react-router-dom";
import ProfileMenu from "../../components/ProfileMenu/ProfileMenu";
import { useSEO } from "../../context/SEOContext";
import { useTranslation } from "react-i18next";
const ChangePassword = () => {
  const { t } = useTranslation();
  const setSeo = useSEO();
  useEffect(() => {
    setSeo({
      title: t("changePassword.meta.title"),
      description: t("changePassword.meta.description"),
      keywords: t("changePassword.meta.keywords"),
      favicon: "/faviconnew.png",
    });
  }, [setSeo, t]);
  const { id } = useParams();
  const navigate = useNavigate();
  const userId = sessionStorage.getItem("user_id");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [email, setEmail] = useState("");
  const [isCodeSent, setIsCodeSent] = useState(false);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    if (userId !== id) {
      Swal.fire({
        icon: "warning",
        title: "Unauthorized Access!",
        text: "You cannot change another user's password.",
      }).then(() => {
        navigate(`/profile/${userId}`);
      });
      return;
    }

    // Check if user is social login - redirect them away from password change
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user?.provider === "google" || user?.provider === "apple") {
          Swal.fire({
            icon: "info",
            title: t("SocialUserNoPasswordTitle") || "Password Not Available",
            text: t("SocialUserNoPasswordText") || "You logged in with a social provider. Password management is handled by your provider.",
            confirmButtonText: t("OK"),
          }).then(() => {
            navigate(`/profile/${userId}`);
          });
          return;
        }
      } catch (e) {
        console.error("Error parsing user data:", e);
      }
    }

    // Fetch user email from API
    fetch(`${process.env.REACT_APP_API_URL}/users?id=${userId}`)
      .then((res) => res.json())
      .then((data) => {
        if (data && data.email) {
          setEmail(data.email);
        }
      })
      .catch((err) => console.error("Failed to fetch user info", err));
  }, [id, userId, navigate, t]);

  const handleSendCode = async (e) => {
    e.preventDefault();

    const payload = {
      id: userId,
      currentPassword,
      newPassword,
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/changePassword`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();

      if (res.ok) {
        setIsCodeSent(true);
        Swal.fire("Verification Code Sent", data.message, "success");
      } else {
        Swal.fire("Error", data.message, "error");
      }
    } catch (err) {
      Swal.fire(
        "Server Error",
        "An error occurred while sending the code",
        "error"
      );
    }
  };

  const handleVerifyCodeAndChangePassword = async () => {
    const payload = {
      email,
      code: verificationCode,
      newPassword,
    };

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/changePassword`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();

      if (res.ok) {
        Swal.fire("Password Changed", data.message, "success");
        setCurrentPassword("");
        setNewPassword("");
        setVerificationCode("");
        setIsCodeSent(false);
      } else {
        Swal.fire("Error", data.message, "error");
      }
    } catch (err) {
      Swal.fire("Server Error", "An error occurred while processing", "error");
    }
  };

  return (
    <>
      <ProfileMenu userId={userId} />
      <div className="container py-5">
        <div className="row">
          <div className="col-lg-6">
            <h2 className="mb-5 titlecolor"> {t("ChangePassword")}</h2>
            <form onSubmit={handleSendCode}>
              <div className="mb-3  position-relative">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  className="form-control  fs-11 p-3"
                  placeholder={t("CurrentPasswordPlaceholder")}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
                <i
                  className={`fa-regular ${
                    showCurrentPassword ? "fa-eye-slash" : "fa-eye"
                  } position-absolute top-50 end-0 translate-middle-y me-3`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                ></i>
              </div>
              <div className="mb-3  position-relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  className="form-control  fs-11 p-3"
                  placeholder={t("NewPasswordPlaceholder")}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <i
                  className={`fa-regular ${
                    showNewPassword ? "fa-eye-slash" : "fa-eye"
                  } position-absolute top-50 end-0 translate-middle-y me-3`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setShowNewPassword(!showNewPassword)}
                ></i>
              </div>
              {!isCodeSent && (
                <button
                  type="submit"
                  className="btn profilebtn fw-light  pe-5 px-5 p-2"
                >
                  {t("SendVerificationCodeButton")}
                </button>
              )}
            </form>

            {isCodeSent && (
              <div className="mt-4">
                <input
                  type="text"
                  className="form-control fs-11 p-3 mb-3 fw-light"
                  placeholder={t("VerificationCodePlaceholder")}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                />
                <button
                  className="btn profilebtn  pe-5 px-5 p-3"
                  onClick={handleVerifyCodeAndChangePassword}
                >
                  {t("VerifyAndUpdatePasswordButton")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ChangePassword;
